<script lang="ts" setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useSessionStore } from "@/stores/session-store";
import { useCurrentOrg, useOrgUsers } from "@/queries/orgs";
import { useActivities } from "@/queries/activities";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import ActivityFeed from "@/views/shared/components/ActivityFeed.vue";
import { useRouter } from "vue-router";
import PageHeader from "@/components/PageHeader.vue";

const router = useRouter();
const sessionStore = useSessionStore();
const { session } = storeToRefs(sessionStore);

const { org, orgLoading: isLoading, orgError, refetchOrg } = useCurrentOrg();
const { orgUsers, orgUsersError, refetchOrgUsers } = useOrgUsers();

// Org-wide recent activity — no category filter; shows everything that happened in this org.
const { activities: recentActivity, activitiesLoading } = useActivities({ take: 10 });

const hasError = computed(() => !!orgError.value || !!orgUsersError.value);

const canManageUsers = computed(() =>
  session.value.isSuperAdmin || session.value.role === "OrgAdmin"
);

// Mock stats - TODO: Replace with actual API calls
const orgStats = computed(() => ({
  totalUsers: orgUsers.value.length,
  totalProjects: 12,
  totalConnections: 8,
  monthlyRuns: 1543,
  storageUsed: "245 MB",
  apiCallsThisMonth: 8765,
}));

function loadOrgData() {
  refetchOrg();
  refetchOrgUsers();
}

function navigateToUsers() {
  router.push("/org/users");
}

function navigateToSettings() {
  router.push("/org/settings");
}

function navigateToProfile() {
  router.push("/org/profile");
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        :title="org?.orgName || 'Organisation'"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Organisation' }
        ]"
      >
        <template #actions>
          <button class="bb-btn-primary-cta" @click="navigateToUsers">
            <i class="bi bi-people me-2"></i><span class="d-none d-md-inline">Manage </span>Users
          </button>
          <button class="bb-btn-secondary-cta" @click="navigateToSettings">
            <i class="bi bi-gear me-2"></i><span class="d-none d-md-inline">Organisation </span>Settings
          </button>
        </template>
      </PageHeader>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        type="general"
        @retry="loadOrgData"
      />

      <!-- Content -->
      <div v-else class="row g-4 mt-1">
        <!-- Organisation Overview Stats -->
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Overview</h2>
              <div class="row g-3 mt-2">
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-people fs-2 text-primary"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.totalUsers }}</div>
                    <div class="text-muted small">Users</div>
                  </div>
                </div>
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-boxes fs-2 text-success"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.totalProjects }}</div>
                    <div class="text-muted small">Projects</div>
                  </div>
                </div>
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-link fs-2 text-info"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.totalConnections }}</div>
                    <div class="text-muted small">Connections</div>
                  </div>
                </div>
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-play-circle fs-2 text-warning"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.monthlyRuns }}</div>
                    <div class="text-muted small">Runs (30d)</div>
                  </div>
                </div>
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-hdd fs-2 text-secondary"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.storageUsed }}</div>
                    <div class="text-muted small">Storage</div>
                  </div>
                </div>
                <div class="col-4 col-xl-2">
                  <div class="stat-card text-center p-3 border rounded">
                    <i class="bi bi-cloud-arrow-up fs-2 text-danger"></i>
                    <div class="fs-4 fw-bold mt-2">{{ orgStats.apiCallsThisMonth }}</div>
                    <div class="text-muted small">API Calls</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Organisation Details + Recent Activity (left, wider) -->
        <div class="col-12 col-lg-8">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Organisation Details</h2>
              <div class="mt-3">
                <div class="mb-3">
                  <label class="text-muted small">Name</label>
                  <div class="fw-semibold">{{ org?.orgName || 'Loading...' }}</div>
                </div>
                <div class="mb-3">
                  <label class="text-muted small">Current User</label>
                  <div class="">{{ session.user?.firstName }} {{ session.user?.lastName }}</div>
                </div>
                <div class="mb-3">
                  <label class="text-muted small">Role</label>
                  <div>
                    <span class="badge bg-primary">Admin</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity — unfiltered for this org. Lives under Org Details in the
               left column so it doesn't span across the Quick Actions sidebar. -->
          <div class="card mt-3">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h2 class="card-title mb-0">Recent Activity</h2>
                <small v-if="activitiesLoading" class="text-muted">Loading…</small>
              </div>
              <ActivityFeed
                :activities="recentActivity"
                :loading="activitiesLoading"
                empty-text="No activity yet. Workflow publishes, project changes, and user joins will appear here."
              />
            </div>
          </div>
        </div>

        <!-- Quick Actions (right, narrower) -->
        <div class="col-12 col-lg-4">
          <div class="card">
            <div class="card-body">
              <h2 class="card-title">Quick Actions</h2>
              <div class="list-group list-group-flush mt-3">
                <button
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  @click="navigateToProfile"
                >
                  <i class="bi bi-person fs-4 me-3 text-primary"></i>
                  <div>
                    <div class="">My Profile</div>
                    <div class="text-muted small">Update your personal settings and preferences</div>
                  </div>
                </button>
                <button
                  v-if="canManageUsers"
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  @click="navigateToUsers"
                >
                  <i class="bi bi-person-plus fs-4 me-3 text-success"></i>
                  <div>
                    <div class="">Invite Users</div>
                    <div class="text-muted small">Add new team members to your organisation</div>
                  </div>
                </button>
                <button
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  @click="navigateToSettings"
                >
                  <i class="bi bi-key fs-4 me-3 text-warning"></i>
                  <div>
                    <div class="">Manage API Keys</div>
                    <div class="text-muted small">View and regenerate your API keys</div>
                  </div>
                </button>
                <button
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  @click="navigateToSettings"
                >
                  <i class="bi bi-gear fs-4 me-3 text-info"></i>
                  <div>
                    <div class="">Organisation Settings</div>
                    <div class="text-muted small">Update name, subscription, and preferences</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelCard>
  </PanelGrid>
</template>

<style scoped lang="scss">
.stat-card {
  transition: all 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transform: translateY(-2px);
}

// At 3-up on mobile the cards are narrow; trim padding and shrink the label so
// longer words (e.g. "Connections") stay on one line.
@include media-breakpoint-down(md) {
  .stat-card {
    padding: 0.75rem 0.25rem !important;

    .small {
      font-size: 0.7rem;
    }
  }
}
</style>
